import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Alert } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

// Load Stripe only when publishable key is set (empty string throws)
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey && stripeKey.trim() ? loadStripe(stripeKey) : null;

const CheckoutForm = ({ selectedPlan }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // Get stored user preferences
    const preferences = JSON.parse(localStorage.getItem('tempUserPreferences') || '{}');

    // Create payment method
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
    });

    if (error) {
      setError(`Payment failed: ${error.message}`);
      setProcessing(false);
      return;
    }

    try {
      // Send payment info to your server
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          planId: selectedPlan.id,
          preferences: preferences,
        }),
      });

      const data = await response.json();

      if (data.error) {
        setError(data.error);
        setProcessing(false);
        return;
      }

      // Handle subscription success
      setSucceeded(true);
      setError(null);
      
      // Navigate to signup with subscription info
      navigate('/signup', {
        state: {
          preferences: preferences,
          subscription: data.subscription
        }
      });
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Payment error:', err);
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-form">
      <div className="form-group mb-4">
        <label htmlFor="card-element" className="mb-2">Credit or debit card</label>
        <div className="card-element-container p-3 border rounded">
          <CardElement id="card-element" options={cardElementOptions} />
        </div>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      
      <div className="mt-3">
        <div className="form-group">
          <label htmlFor="promo-code" className="mb-2">Promo Code</label>
          <div className="d-flex gap-2">
            <input
              type="text" 
              id="promo-code"
              className="form-control"
              placeholder="Enter promo code"
            />
            <Button 
              // variant="outline-primary"
              // className="btn-primary"
              onClick={() => {}} // TODO: Implement promo code validation
            >
              Apply
            </Button>
          </div>
        </div>
        <Button 
        type="submit" 
        disabled={processing || !stripe || succeeded}
        className="w-100 mt-5 btn-primary"
        style={{borderRadius: '25px', padding: '0.8rem'}}
      >
        {processing ? 'Processing...' : `Confirm ${selectedPlan.name}`}
      </Button>
      </div>
    </form>
  );
};

const Payment = ({ selectedPlan }) => {
  if (!selectedPlan) {
    selectedPlan = {
      id: 'price_monthly',
      name: 'Monthly Plan',
      price: '$9.99/month'
    };
  }

  if (!stripePromise) {
    return (
      <div className="payment-form-container">
        <Alert variant="info">Payment is not configured. Please contact support@getautochef.com.</Alert>
      </div>
    );
  }

  return (
    <div className="payment-form-container">
      <Elements stripe={stripePromise}>
        <CheckoutForm selectedPlan={selectedPlan} />
      </Elements>
    </div>
  );
};

export default Payment;
